import { Injectable } from '@angular/core';

/**
 * Scripts provider.
 * Manages dynamic loading of external scripts.
 *
 * This provider allows loading scripts at runtime, which is useful for:
 * - Loading third-party scripts dynamically
 * - Environment-specific script URLs
 * - Lazy loading of non-critical scripts
 */
@Injectable({ providedIn: 'root' })
export class ScriptsProvider {
  private loadedScripts = new Map<string, Promise<void>>();

  /**
   * Loads a script dynamically and returns a promise that resolves when the script is loaded.
   * If the script is already loaded or being loaded, returns the existing promise.
   *
   * @param src - The URL of the script to load
   * @param type - The script type (default: 'module')
   * @returns Promise that resolves when the script is loaded
   *
   * @example
   * ```typescript
   * await scriptsProvider.loadScript('https://example.com/script.js');
   * ```
   */
  loadScript(src: string, type: 'module' | 'text/javascript' = 'module'): Promise<void> {
    // Return existing promise if script is already loaded or loading
    if (this.loadedScripts.has(src)) {
      return this.loadedScripts.get(src)!;
    }

    const promise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.type = type;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        this.loadedScripts.delete(src);
        reject(new Error(`Failed to load script: ${src}`));
      };

      document.head.appendChild(script);
    });

    this.loadedScripts.set(src, promise);
    return promise;
  }

  /**
   * Checks if a script has been loaded.
   *
   * @param src - The URL of the script to check
   * @returns True if the script has been loaded
   */
  isScriptLoaded(src: string): boolean {
    return this.loadedScripts.has(src);
  }

  /**
   * Loads multiple scripts sequentially.
   * Scripts are loaded one after another to maintain order.
   *
   * @param scripts - Array of script URLs to load
   * @returns Promise that resolves when all scripts are loaded
   */
  async loadScriptsSequentially(scripts: string[]): Promise<void> {
    for (const src of scripts) {
      await this.loadScript(src);
    }
  }

  /**
   * Loads multiple scripts in parallel.
   * All scripts are loaded simultaneously for better performance.
   *
   * @param scripts - Array of script URLs to load
   * @returns Promise that resolves when all scripts are loaded
   */
  async loadScriptsParallel(scripts: string[]): Promise<void> {
    await Promise.all(scripts.map(src => this.loadScript(src)));
  }
}
